import React, { useEffect, useRef } from "react";
import { FormProvider } from "react-hook-form";
import { Button, CircularProgress, Form, useThemeMode } from "@sscs/ui";
import { LoginContentWrapper } from "Components";
import { getReCaptchaSitekey } from "Utils";
import loginLayoutStyles from "../../../../components/LoginLayout/loginLayout.module.scss";
import { useRecaptcha, useRequestResetForm } from "../../hooks";
import ConfirmReset from "../ConfirmReset";
import styles from "./requestResetForm.module.scss";

const RECAPTCHA_CONTAINER_ID = "recaptcha-container-id";
const RECAPTCHA_SITE_KEY = getReCaptchaSitekey();

// Google's reCAPTCHA "normal" widget renders at a hard-coded 304px; scaling it
// to the container width is the only way to make it fill the card like the
// other fields, since the iframe's contents can't be restyled.
const RECAPTCHA_WIDGET_WIDTH = 304;

const RequestResetForm = (): React.JSX.Element => {
  const { mode } = useThemeMode();
  const recaptchaRef = useRef<HTMLDivElement>(null);

  const { reCaptchaResponseToken, widgetRendered } = useRecaptcha({
    containerId: RECAPTCHA_CONTAINER_ID,
    sitekey: RECAPTCHA_SITE_KEY,
    theme: mode,
  });

  useEffect(() => {
    const container = recaptchaRef.current;
    if (!container || !widgetRendered) return;

    const applyScale = () => {
      const scale = container.offsetWidth / RECAPTCHA_WIDGET_WIDTH;
      container.style.setProperty("--recaptcha-scale", String(scale));
    };

    applyScale();
    const observer = new ResizeObserver(applyScale);
    observer.observe(container);
    return () => observer.disconnect();
  }, [widgetRendered]);

  const { errors, isSubmitSuccessful, isValid, onSubmit, methods } =
    useRequestResetForm(reCaptchaResponseToken);

  if (isSubmitSuccessful) return <ConfirmReset />;

  return (
    <LoginContentWrapper
      title="Reset your password"
      byline="Enter your verified email address. If your account exists, we'll send you a password reset link."
    >
      <FormProvider {...methods}>
        <Form.Form onSubmit={onSubmit}>
          <Form.TextField
            type="email"
            label="Email address"
            name="email"
            autoComplete="email"
            isRequired
            errors={errors}
            isFullWidth
          />
          {!widgetRendered && <CircularProgress className={styles.progress} />}
          <div
            id={RECAPTCHA_CONTAINER_ID}
            ref={recaptchaRef}
            className={styles.recaptcha}
          />
          <div className={loginLayoutStyles["form-button"]}>
            <Button
              isFullWidth
              type="submit"
              color="primary"
              disabled={!reCaptchaResponseToken || !isValid}
            >
              Send password reset email
            </Button>
          </div>
        </Form.Form>
      </FormProvider>
    </LoginContentWrapper>
  );
};

export default RequestResetForm;
